import type { Page, Stroke, UUID } from '../model/types';
import { buildOutline, polygonBounds, tracePolygon, type Rect } from './render/geometry';
import { Grid } from './spatial/Grid';

/** Render-side view of a stored stroke. The stroke record itself is never mutated here. */
export interface RenderStroke {
  stroke: Stroke | Omit<Stroke, 'rev'>;
  seq: number;
  bounds: Rect;
  poly: number[] | null;
  path: Path2D | null;
}

let seqCounter = 0;

/** In-memory state of one page: its strokes and a spatial index of the visible ones. */
export class PageModel {
  readonly strokes = new Map<UUID, RenderStroke>();
  readonly grid = new Grid<RenderStroke>(100);

  constructor(public page: Page) {}

  /** Adds or replaces a stroke. Deleted strokes are kept but not indexed. */
  put(stroke: Stroke | Omit<Stroke, 'rev'>): RenderStroke {
    const old = this.strokes.get(stroke.id);
    if (old) this.grid.remove(old);
    const poly = buildOutline(stroke.points, stroke);
    const rs: RenderStroke = {
      stroke,
      seq: old?.seq ?? ++seqCounter,
      bounds: poly.length ? polygonBounds(poly) : { minX: 0, minY: 0, maxX: 0, maxY: 0 },
      poly,
      path: null,
    };
    this.strokes.set(stroke.id, rs);
    if (stroke.deletedAt === undefined && poly.length) this.grid.insert(rs, rs.bounds);
    return rs;
  }

  /** Visible strokes intersecting `r`, in paint order: highlighters first, then pen, each by creation. */
  query(r: Rect): RenderStroke[] {
    return [...this.grid.query(r)].sort((a, b) => {
      const ha = a.stroke.tool === 'highlighter' ? 0 : 1;
      const hb = b.stroke.tool === 'highlighter' ? 0 : 1;
      return ha - hb || a.stroke.startTime - b.stroke.startTime || a.seq - b.seq;
    });
  }

  get visibleCount(): number {
    return this.grid.size;
  }
}

/** Lazily builds (and caches) a stroke's Path2D. Browser only. */
export function pathOf(rs: RenderStroke): Path2D {
  if (!rs.path) {
    const p = new Path2D();
    tracePolygon(p, rs.poly ?? []);
    rs.path = p;
    rs.poly = null;
  }
  return rs.path;
}
